import React, {useEffect, useRef} from 'react';
import {useNavigate} from 'react-router-dom';
import {useTranslation} from 'react-i18next';
import {toast} from 'react-toastify';
import {Spinner} from '../../Spinner';
import {ClientEconomicsApi} from '../../../api/clientEconomicsApi';
import {EscalafyChannelApi, payloadOf} from '../../../api/escalafyChannelApi';
import {LocalStorage} from '../../../utils/LocalStorage';
import {
    callbackErrorKey,
    clearPendingEscalafyConnection,
    ESCALAFY_FLOW_PORTAL,
    escalafyRedirectUri,
    escalafyReturnTo,
    markPendingEscalafyPosted,
    readPendingEscalafyConnection,
    setPendingPortalPick,
} from './state/escalafyModel';

// El usuario apretó «Cancelar» en Escalafy: no es un error, se vuelve sin avisar nada (R-ecofe-10).
const OAUTH_ACCESS_DENIED = 'access_denied';

/**
 * La vuelta del OAuth de Escalafy (`/escalafy/callback?code=…&state=…`), de la agencia Y del portal.
 *
 * Postea `code` + `state` al backend con el token de la sesión —como todos los canales—, que valida
 * la firma y el intento (15 min, un solo uso), canjea el código con el `code_verifier` que guardó él
 * y trae las organizaciones. Acá sólo se decide a qué callback postear y a dónde volver, por la nota de
 * sessionStorage (`flow`, contrato «Escalafy como canal» §6):
 *
 * - **Agencia desde Economía** (`clientId`): vuelve a la economía del cliente con la credencial para
 *   reabrir el asistente en el paso 2. Una RECONEXIÓN (`reconnected`) vuelve sin abrir nada.
 * - **Agencia desde Conexiones** (sin `clientId`): vuelve a Conexiones; si se tipeó un nombre para la
 *   conexión, se le pone antes de volver.
 * - **Portal** (`flow: 'portal'`): postea al callback del portal, que NO vincula nada, y vuelve a
 *   `/portal/connections` con las organizaciones para que el dueño elija qué tienda comparte.
 *
 * La nota se borra al SALIR, no al leerla: un refresh con el canje en viaje no lo repostea (el `code` es
 * de un solo uso y el segundo POST perdía el intento), vuelve a la pantalla de origen.
 *
 * **Sin nota** (venció a los 15 minutos, otra pestaña, sin sessionStorage) decide el tipo de usuario: el del portal
 * no postea al callback de la agencia —que lo rechazaría— ni puede postear al del portal sin saber a qué cliente
 * va; avisa y vuelve a su pantalla. La agencia canjea como siempre y vuelve a Conexiones, donde queda la conexión.
 */
export function EscalafyCallback() {
    const {t} = useTranslation();
    const navigate = useNavigate();
    const started = useRef(false);

    useEffect(() => {
        // StrictMode monta dos veces: el código de autorización es de un solo uso.
        if (started.current) return;
        started.current = true;

        const params = new URLSearchParams(window.location.search);
        const code = params.get('code');
        const state = params.get('state');
        const pending = readPendingEscalafyConnection();
        const isPortalUser = LocalStorage.fetchUserType() === 'CLIENT_USER';
        const fromPortal = pending ? pending.flow === ESCALAFY_FLOW_PORTAL : isPortalUser;

        const leave = (credentialId = null) => {
            clearPendingEscalafyConnection();
            navigate(escalafyReturnTo(pending, credentialId, isPortalUser), {replace: true});
        };
        const fail = (response = null) => {
            // Mensaje propio, nunca el del backend: en el portal, `CONNECTION_NOT_AVAILABLE` no dice por qué.
            toast.error(t(fromPortal ? 'portal_connections.connect_error' : callbackErrorKey(response)));
            leave();
        };

        if (!code || !state) {
            if (params.get('error') === OAUTH_ACCESS_DENIED) leave();
            else fail();
            return;
        }
        // Refresh (o «atrás») con el canje ya mandado: no se repostea el `code`.
        if (pending && pending.posted) {
            leave();
            return;
        }
        // Portal sin nota: no hay a qué cliente mandar la conexión.
        if (fromPortal && !pending) {
            fail();
            return;
        }

        markPendingEscalafyPosted();
        const redirectUri = escalafyRedirectUri();

        if (fromPortal) {
            EscalafyChannelApi.portalCallback({code, state, redirectUri, agencyClientId: pending.agencyClientId})
                .then((response) => {
                    const data = payloadOf(response);
                    // Sin tiendas que elegir (la cuenta no tiene, o Escalafy no pudo listarlas: `organizations_error`)
                    // no se abre un modal vacío: se avisa y se vuelve. La credencial sin tiendas el BE la muestra
                    // «sin conectar» y la revoca sola.
                    const organizations = data && Array.isArray(data.organizations) ? data.organizations : [];
                    if (!data || !data.credential_id || data.organizations_error || organizations.length === 0) {
                        fail(response);
                        return;
                    }
                    setPendingPortalPick({
                        credentialId: data.credential_id,
                        agencyClientId: pending.agencyClientId,
                        organizations,
                    });
                    leave();
                }).catch(() => fail());
            return;
        }

        ClientEconomicsApi.escalafyCallback({code, state, redirectUri}).then((response) => {
            const data = response && response.status === 'OK' ? response.data : null;
            if (!data || !data.credential_id) {
                fail(response);
                return;
            }
            if (data.reconnected === true) {
                toast.success(t('economics.escalafy.reconnected'));
                leave();
                return;
            }
            // Se pidió reconectar y el backend no pudo confirmar que fuera la misma cuenta: quedó una
            // credencial NUEVA y la caída sigue caída. Se dice, y se sigue al paso 2 con la nueva.
            if (data.reconnect_warning) toast.warning(t('economics.escalafy.reconnect_new_account'));
            // Desde Conexiones, la conexión lleva el nombre que se tipeó en el modal. Si no se puede
            // poner, queda el del backend (el email de Escalafy): no es un error que haya que avisar.
            const label = pending && !pending.clientId ? pending.label : null;
            if (label && data.agency_channel_credential_id) {
                EscalafyChannelApi.renameCredential(data.agency_channel_credential_id, label)
                    .catch(() => null)
                    .then(() => leave(data.credential_id));
                return;
            }
            leave(data.credential_id);
        }).catch(() => fail());
    }, [navigate, t]);

    return <div className="box-loader"><Spinner color="positive"/></div>;
}

export default EscalafyCallback;
